<script setup lang="ts">
// import TopBarSection from '@/components/topbar-section/TopBarSection.component.vue'
// import FooterSection from '@/components/footer-section/FooterSection.component.vue'
// import { useThemeStore } from '@/components/theme-selector/themes-store'
// onMounted(() => {
//   if (import.meta.client && document) {
//     useThemeStore().restoreTheme()
//   }
// })

import AppHeader from '@/components/app-header/AppHeader.vue'
import SidebarDesktop from '@/components/sidebar/SidebarDesktop.vue'
import SidebarMobile from '@/components/sidebar/SidebarMobile.vue'

import { useDashboardStore } from '@tenorlab/vue-dashboard'
import { useDashboardStorageService } from '@tenorlab/vue-dashboard/core'
import { getDashboardDefaults } from '@/dashboard-defaults'
import { getAdditionalDashboardsForDemo } from '@/dashboard-customs-demo'
import { AppActionType, menuItemIds } from '@/models/'
import { useConfig } from '@/config/'
import { useNavigationStore } from '@/state/'
import type { INavigationItem } from '@/models/'

const config = useConfig()
const clientAppKey = config.global.clientAppKey
const user = { id: 1234 }
const userId = user.id
const dashboardStore = useDashboardStore()
const dashboardStorageService = useDashboardStorageService()
const navigationStore = useNavigationStore()
//const navigationStore = useNavigationStore()

const isEditing = false

const {
  // isLoading,
  dashboardMenuItem,
  navigationItems,
  secondaryNavigationItems,
  currentNavigationItem,
  isDesktopSidebarCollapsed,
  isMobileSidebarOpen,
} = navigationStore.computed

const menuItemId = computed(() => {
  return currentNavigationItem?.value?.id || 'default'
})

const onSidebarMenuItemClicked = async (item: INavigationItem) => {
  await navigationStore.selectMenuItemById(item.id)

  // NUXT: navigate to uxt route:
  if (item.parentId === menuItemIds.dashboards) {
    if (`${item.id}` === 'default') {
      await navigateTo('/dashboards/')
    } else {
      await navigateTo(`/dashboards/${item.id}`)
    }
  } else {
    await navigateTo(`/${item.id}`)
  }
}
const onToggleDesktopSidebarCollapsed = async () => {
  await navigationStore.toggleDesktopSidebarCollapsed()
}
const closeMobileSidebar = async () => {
  await navigationStore.toggleMobileSidebarOpen()
}
const onExpandOrCollapseNavigationItem = async (item: INavigationItem) => {
  await navigationStore.expandOrCollapseNavigationItem(item)
}

const onAppHeaderAction = async (actionInfo: { type: AppActionType; args?: any }) => {
  const actionType = actionInfo.type
  switch (actionType) {
    case AppActionType.toggleMobileSidebarOpen: {
      //await navigationStore.toggleMobileSidebarOpen()
      break
    }
    case AppActionType.toggleSidebarCollapsed: {
      //await navigationStore.toggleDesktopSidebarCollapsed()
      break
    }
    case AppActionType.navigate: {
      //await navigationStore.selectMenuItemById(actionInfo.args as any)
      break
    }
    default: {
      console.warn('onAppHeaderAction: UNHANDLED action', actionInfo)
    }
  }
}

const isDynamicDashboard = computed(() => {
  // if parent menu item is "Dashboards", and the currentMenuItemId matches a dashbaordId, then is a dynamic dashboard:
  const currentParentMenuItemId = currentNavigationItem.value?.parentId
  const currentMenuItemId = currentNavigationItem.value?.id
  return (
    currentParentMenuItemId === menuItemIds.dashboards
    // && dashboardStore.computed.allDashboardConfigs.value.some(
    //   (d) => d.dashboardId === currentMenuItemId,
    // )
  )
})

// nuxt route:
const route = useRoute()

const restoreSelectedMenuItemId = async () => {
  // const routePathToMenuItemId = new Map([
  //   ['/', menuItemIds.home],
  //   ['/about', menuItemIds.about],
  //   ['/anotherpage', menuItemIds.anotherpage],
  //   ['/dashboards', menuItemIds.dashboards],
  // ])

  let path = route.path
  if (path.indexOf('/dashboards/') > -1) {
    const menuItemId = path.replace('/dashboards', '').replaceAll('/', '')
    if (menuItemId.length > 0) {
      useNavigationStore().selectMenuItemById(menuItemId)
    }
  } else if (path.length > 0) {
    path = path.startsWith('/') ? path.substring(1) : path
    let menuItemId = path.endsWith('/') ? path.substring(0, path.length - 1) : path
    useNavigationStore().selectMenuItemById(menuItemId || menuItemIds.home)
  }
}

onMounted(async () => {
  async function initDashboards() {
    // 1. Start Loading immediately
    dashboardStore.setIsLoading(true)

    try {
      // 2. Fetch Defaults (Catalog and Default Config)
      const defaults = await getDashboardDefaults(user, clientAppKey)

      // 3. Fetch Saved Configs from Storage
      let savedConfigs = await dashboardStorageService.getSavedDashboards(
        userId,
        clientAppKey,
        defaults.widgetsCatalog,
        defaults.dashboardConfig,
      )

      // if savedConfigs is only one (default), add additional pre-defined custom dashboards for demo
      if (savedConfigs.length < 2) {
        const demoCustomDashboards = await getAdditionalDashboardsForDemo(user, clientAppKey)
        savedConfigs.push(...demoCustomDashboards)

        await dashboardStorageService.saveDashboards(
          userId,
          clientAppKey,
          savedConfigs,
          defaults.widgetsCatalog,
        )
      }

      // 4. Batch the Store Updates
      // We set everything at once before turning off isLoading
      dashboardStore.setAllDashboardConfigs(savedConfigs)

      const dashboardConfig =
        savedConfigs.find((x) => x.dashboardId === 'default') || savedConfigs[0] || defaults.dashboardConfig

      dashboardStore.setCurrentDashboardConfig(dashboardConfig)

      // init navigation state with dynamically added dashboards
      await navigationStore.initState(savedConfigs)

      // initially, select the default dashboard programmatically
      await navigationStore.selectMenuItemById('default')
    } catch (error) {
      console.error('Dashboard Init Failed', error)
    } finally {
      // 6. FINALLY stop loading - this triggers the transition to DashboardGrid
      dashboardStore.setIsLoading(false)
    }
  }

  await initDashboards()
  await restoreSelectedMenuItemId()
})
</script>

<template>
  <div
    class="relative flex flex-col w-full grow"
    style="scroll-behavior: smooth; scroll-snap-type: y proximity"
  >
    <ClientOnly>
      <noscript>
        <strong
          >We're sorry but this website doesn't work properly without JavaScript enabled. Please enable it to
          continue.</strong
        >
      </noscript>

      <SidebarMobile
        :isOpen="isMobileSidebarOpen"
        :isEditing="isEditing"
        :config="config"
        :dashboardMenuItem="dashboardMenuItem"
        :navigationItems="navigationItems"
        :secondaryNavigationItems="secondaryNavigationItems"
        @menuItemClicked="onSidebarMenuItemClicked"
        @expandOrCollapse="onExpandOrCollapseNavigationItem"
        @close="closeMobileSidebar"
      />

      <SidebarDesktop
        v-if="!isMobileSidebarOpen"
        :isCollapsed="isDesktopSidebarCollapsed"
        :isEditing="isEditing"
        :config="config"
        :dashboardMenuItem="dashboardMenuItem"
        :navigationItems="navigationItems"
        :secondaryNavigationItems="secondaryNavigationItems"
        @expandOrCollapse="onExpandOrCollapseNavigationItem"
        @menuItemClicked="onSidebarMenuItemClicked"
        @toggleSidebarCollapsed="onToggleDesktopSidebarCollapsed"
      />

      <div
        :class="`flex flex-col h-full flex-1 transition-all duration-150 ${isDesktopSidebarCollapsed ? 'lg:pl-14' : 'lg:pl-64'}`"
        style="scroll-margin: 0; scroll-snap-type: y proximity"
      >
        <AppHeader
          :loading="false"
          :isCollapsed="isDesktopSidebarCollapsed"
          :isEditing="isEditing"
          :config="config"
          @action="onAppHeaderAction"
        />
        <main class="flex-1 h-full" style="scroll-margin: 0; scroll-snap-type: y proximity">
          <div class="view-wrapper flex flex-col grow h-full gap-y-2 sm:gap-y-4 p-2 sm:p-4">
            <NuxtPage :isDynamicDashboard="isDynamicDashboard" :menuItemId="menuItemId" />
          </div>
        </main>
      </div>
    </ClientOnly>
  </div>
</template>
